import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Paperclip, Download, Upload, HardHat } from "lucide-react";
import { fmtDate, fmtDateTime, fmtSize } from "@/lib/assignments";

export const VISIT_OUTCOMES = ["Sopralluogo effettuato", "Cliente assente", "Appuntamento rinviato", "Appuntamento annullato", "Altro"];
const outTone = (o) => o === "Sopralluogo effettuato" ? "bg-success/10 text-success" : o === "Cliente assente" ? "bg-danger/10 text-danger" : o === "Appuntamento rinviato" ? "bg-warning/15 text-warning" : "bg-slate-100 text-slate-600";

export default function SiteVisitsTab({ assignment, canManage, onChanged }) {
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const id = assignment.id;
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/assignments/${id}/site-visits`); setRows(data); }
    catch { setRows([]); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="visits-tab">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500">{rows ? `${rows.length} sopralluoghi registrati` : ""}</span>
        {canManage && (
          <Button size="sm" className="gap-1.5" onClick={() => setShowNew(true)} data-testid="visit-new">
            <Plus className="h-3.5 w-3.5" /> Registra sopralluogo
          </Button>
        )}
      </div>
      {rows === null ? (
        <div className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-slate-400" data-testid="visits-empty">Nessun sopralluogo registrato.</p>
      ) : (
        <ol className="space-y-2" data-testid="visits-list">
          {rows.map((v) => <VisitCard key={v.id} v={v} assignmentId={id} canManage={canManage} onChanged={() => { load(); onChanged?.(); }} />)}
        </ol>
      )}
      {showNew && <NewVisitDialog assignment={assignment} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); onChanged?.(); }} />}
    </div>
  );
}

function VisitCard({ v, assignmentId, canManage, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/assignments/${assignmentId}/site-visits/${v.id}/attachments`, fd);
      toast.success("Allegato caricato"); onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload non riuscito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/assignments/${assignmentId}/site-visits/${v.id}/attachments/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };
  return (
    <li className="rounded-md border border-slate-100 px-3 py-2" data-testid={`visit-${v.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <HardHat className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">{fmtDate(v.visit_date)}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${outTone(v.outcome)}`} data-testid={`visit-outcome-${v.id}`}>{v.outcome}</span>
        </div>
        <span className="text-[11px] text-slate-500">{v.technician_name ? `Tecnico: ${v.technician_name} · ` : ""}registrato da {v.created_by_name || "—"} · {fmtDateTime(v.created_at)}</span>
      </div>
      <dl className="mt-2 space-y-1 text-xs">
        {v.observations && <Row k="Osservazioni tecniche" v={v.observations} />}
        {v.works_needed && <Row k="Lavorazioni necessarie" v={v.works_needed} />}
        {v.next_step && <Row k="Prossimo intervento" v={v.next_step} />}
      </dl>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(v.attachments || []).map((d) => (
          <button key={d.id} onClick={() => download(d)} className="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-700 hover:text-brand" data-testid={`visit-attachment-${d.id}`}>
            <Download className="h-3 w-3" /> <span className="max-w-[160px] truncate">{d.filename}</span> <span className="text-slate-400">{fmtSize(d.file_size)}</span>
          </button>
        ))}
        {canManage && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={upload} data-testid={`visit-attachment-input-${v.id}`} />
            <Button variant="outline" size="sm" className="h-7 gap-1 text-[11px]" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`visit-attachment-upload-${v.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Foto / documenti
            </Button>
          </>
        )}
        {!canManage && !(v.attachments || []).length && <span className="flex items-center gap-1 text-[11px] text-slate-400"><Paperclip className="h-3 w-3" /> Nessun allegato</span>}
      </div>
    </li>
  );
}

function Row({ k, v }) {
  return (<div><dt className="font-medium text-slate-500">{k}</dt><dd className="whitespace-pre-wrap text-slate-800">{v}</dd></div>);
}

function NewVisitDialog({ assignment, onClose, onCreated }) {
  const [form, setForm] = useState({ visit_date: new Date().toISOString().slice(0, 10), outcome: "Sopralluogo effettuato", technician_name: "", observations: "", works_needed: "", next_step: "", appointment_id: assignment.appointment?.id || "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.visit_date) { toast.error("Indica la data"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${assignment.id}/site-visits`, {
        visit_date: form.visit_date, outcome: form.outcome, technician_name: form.technician_name || undefined,
        appointment_id: form.appointment_id || undefined, observations: form.observations || undefined,
        works_needed: form.works_needed || undefined, next_step: form.next_step || undefined,
      });
      if (files.length) {
        const fd = new FormData(); files.forEach((f) => fd.append("files", f));
        await api.post(`/assignments/${assignment.id}/site-visits/${data.id}/attachments`, fd);
      }
      toast.success("Sopralluogo registrato");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="visit-dialog">
        <DialogHeader><DialogTitle>Registra sopralluogo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-500">Data *</label><Input type="date" value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} data-testid="visit-date" /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-500">Tecnico / consulente</label><Input value={form.technician_name} onChange={(e) => set("technician_name", e.target.value)} placeholder="Es. Stefano Celestini" data-testid="visit-technician" /></div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Esito *</label>
            <Select value={form.outcome} onValueChange={(v) => set("outcome", v)}>
              <SelectTrigger data-testid="visit-outcome"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{VISIT_OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            {assignment.appointment && (
              <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!form.appointment_id} onChange={(e) => set("appointment_id", e.target.checked ? assignment.appointment.id : "")} data-testid="visit-link-appointment" />
                Collega all'appuntamento del {assignment.appointment.when} (aggiorna il suo stato; la pratica resta aperta)
              </label>
            )}
          </div>
          <Textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} placeholder="Osservazioni tecniche (es. verificato tetto e spazio per batterie)" rows={2} data-testid="visit-observations" />
          <Textarea value={form.works_needed} onChange={(e) => set("works_needed", e.target.value)} placeholder="Lavorazioni necessarie" rows={2} data-testid="visit-works" />
          <Input value={form.next_step} onChange={(e) => set("next_step", e.target.value)} placeholder="Prossimo intervento da eseguire (es. preparare preventivo 6 kW con accumulo)" data-testid="visit-next-step" />
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Foto / documenti (PDF, JPG, PNG, DOC, XLS · max 15 MB)</label>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="visit-files" className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-3 file:py-1.5 file:text-sm file:text-brand" />
            {files.length > 0 && <p className="mt-1 text-xs text-slate-500">{files.length} file selezionato/i</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="visit-submit">{busy ? "Salvataggio…" : "Registra"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
